import { useSystemStore } from './stores/system'

/**
 * Query parameters understood on any page of the hub, used by inbound deep links
 * from external systems (e.g. the LeadPro "More Info" link on a potential referral).
 */
export const OFFICE_ID_PARAM = '__office_id'
export const APPLICANT_ID_PARAM = 'applicant_id'
export const REFERRER_PARAM = 'referrer'
export const REFERRER_LEADPRO = 'leadpro'

export default {
  install: (app) => {
    app.config.globalProperties.$helpers = {
      moveItemUp(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index - 1, 0, item)
      },
      moveItemDown(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index + 1, 0, item)
      },
      removeItem(item, collection) {
        const index = collection.indexOf(item)
        if (index >= 0) {
          collection.splice(collection.indexOf(item), 1)
        }
      },
      isValidUrl(url) {
        return /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(:[0-9]+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/.test(
          url
        )
      },
      sanitiseVideoUrl(url) {
        let matches = /^https?:\/\/(?:www.)?(?:youtu.be\/|youtube.com\/watch\?.*v=)([a-zA-z0-9_-]+)/.exec(url)
        if (matches?.length > 0) {
          return `https://www.youtube.com/embed/${matches[1]}`
        }
        matches =
          /^https?:\/\/(?:www.|player.)?vimeo.com(?:\/.*\/review|\/.*\/download|\/video|\/external)?\/(\d*)/.exec(url)
        if (matches?.length > 0) {
          return `https://player.vimeo.com/video/${matches[1]}`
        }

        return url
      },
      textColor(name) {
        switch (name) {
          case 'red':
            return 'text-red-600'
          case 'orange':
            return 'text-orange-600'
          case 'amber':
            return 'text-amber-600'
          case 'yellow':
            return 'text-yellow-600'
          case 'lime':
            return 'text-lime-600'
          case 'green':
            return 'text-green-600'
          case 'emerald':
            return 'text-emerald-600'
          case 'teal':
            return 'text-teal-600'
          case 'cyan':
            return 'text-cyan-600'
          case 'sky':
            return 'text-sky-600'
          case 'blue':
            return 'text-blue-600'
          case 'indigo':
            return 'text-indigo-600'
          case 'violet':
            return 'text-violet-600'
          case 'purple':
            return 'text-purple-600'
          case 'fuchsia':
            return 'text-fuchsia-600'
          case 'pink':
            return 'text-pink-600'
          case 'rose':
            return 'text-rose-600'
          case 'accent':
            return 'text-accent-500'
          default:
            return 'text-black dark:text-white'
        }
      },
      borderColor(name) {
        switch (name) {
          case 'red':
            return 'border-red-600'
          case 'orange':
            return 'border-orange-600'
          case 'amber':
            return 'border-amber-600'
          case 'yellow':
            return 'border-yellow-600'
          case 'lime':
            return 'border-lime-600'
          case 'green':
            return 'border-green-600'
          case 'emerald':
            return 'border-emerald-600'
          case 'teal':
            return 'border-teal-600'
          case 'cyan':
            return 'border-cyan-600'
          case 'sky':
            return 'border-sky-600'
          case 'blue':
            return 'border-blue-600'
          case 'indigo':
            return 'border-indigo-600'
          case 'violet':
            return 'border-violet-600'
          case 'purple':
            return 'border-purple-600'
          case 'fuchsia':
            return 'border-fuchsia-600'
          case 'pink':
            return 'border-pink-600'
          case 'rose':
            return 'border-rose-600'
          case 'accent':
            return 'border-accent-500'
          default:
            return 'border-black dark:border-white'
        }
      },
      bgColor(name) {
        switch (name) {
          case 'red':
            return 'bg-red-600'
          case 'orange':
            return 'bg-orange-600'
          case 'amber':
            return 'bg-amber-600'
          case 'yellow':
            return 'bg-yellow-600'
          case 'lime':
            return 'bg-lime-600'
          case 'green':
            return 'bg-green-600'
          case 'emerald':
            return 'bg-emerald-600'
          case 'teal':
            return 'bg-teal-600'
          case 'cyan':
            return 'bg-cyan-600'
          case 'sky':
            return 'bg-sky-600'
          case 'blue':
            return 'bg-blue-600'
          case 'indigo':
            return 'bg-indigo-600'
          case 'violet':
            return 'bg-violet-600'
          case 'purple':
            return 'bg-purple-600'
          case 'fuchsia':
            return 'bg-fuchsia-600'
          case 'pink':
            return 'bg-pink-600'
          case 'rose':
            return 'bg-rose-600'
          case 'accent':
            return 'bg-accent-500'
          default:
            return 'bg-black dark:bg-white'
        }
      },
      getModuleList(pageId, deep = false) {
        const systemStore = useSystemStore()
        const page = systemStore.routes.find((r) => r.id === pageId)
        if (!page) {
          return []
        }

        let modules = page.modules.map((m) => m.component)
        if (deep) {
          const children = systemStore.routes.filter((r) => r.parent_id === pageId)
          for (let i = 0; i < children.length; i++) {
            modules = modules.concat(this.getModuleList(children[i].id, deep))
          }
        }

        return modules
      },
      pageIsCallToAction(pageId, deep = false) {
        const systemStore = useSystemStore()
        const moduleList = this.getModuleList(pageId, deep)
        const callToActions = systemStore.callToActions.map((c) => c.component)

        return moduleList.filter((m) => callToActions.includes(m)).length > 0
      }
    }
  }
}
